import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import {
  fetchAdminJson,
  AdminDisclosureCard,
  IntegrationStatusCard,
} from "../settingsHelpers";

export { IntegrationStatusCard };

const CAMPAIGN_TYPE_OPTIONS = [
  { value: "b2c", label: "B2C" },
  { value: "franchise", label: "Franchise" },
];

function campaignTypeBadge(type) {
  if (type === "franchise") {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
        background: "#fdf4ff", color: "#7c3aed", border: "1px solid #e9d5ff",
      }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#7c3aed", display: "inline-block" }} />
        Franchise
      </span>
    );
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#15803d", display: "inline-block" }} />
      B2C
    </span>
  );
}

const EMPTY_CAMPAIGN_FORM = { campaign_id: "", name: "", campaign_type: "b2c", is_active: true };
const EMPTY_FORM_FORM = { form_id: "", name: "" };
const PAGE_SIZE = 20;

function isWithinDateRange(value, from, to) {
  if (!from && !to) return true;
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  if (from && date < new Date(`${from}T00:00:00`)) return false;
  if (to && date > new Date(`${to}T23:59:59.999`)) return false;
  return true;
}

function sortRecords(records, getDate, order) {
  return [...records].sort((left, right) => {
    const leftTime = new Date(getDate(left) || 0).getTime() || 0;
    const rightTime = new Date(getDate(right) || 0).getTime() || 0;
    return order === "oldest" ? leftTime - rightTime : rightTime - leftTime;
  });
}

function MetaPagination({ total, page, onPageChange }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (total <= PAGE_SIZE) return null;
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  const pageNumbers = Array.from({ length: pages }, (_, index) => index + 1)
    .filter((number) => number === 1 || number === pages || Math.abs(number - page) <= 1);

  return <div className="meta-pagination">
    <span>{start}-{end} of {total}</span>
    <button type="button" className="secondary-ghost compact-button" onClick={() => onPageChange(page - 1)} disabled={page === 1}>Previous</button>
    {pageNumbers.map((number, index) => <React.Fragment key={number}>
      {index > 0 && number - pageNumbers[index - 1] > 1 && <span className="meta-pagination-gap">...</span>}
      <button type="button" className={`secondary-ghost compact-button ${number === page ? "active" : ""}`} onClick={() => onPageChange(number)}>{number}</button>
    </React.Fragment>)}
    <button type="button" className="secondary-ghost compact-button" onClick={() => onPageChange(page + 1)} disabled={page === pages}>Next</button>
  </div>;
}

function campaignHealthBadge(status, accessible, formsAllOk) {
  if (!status) return null;
  if (status === "ERROR" && formsAllOk) return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#b45309", display: "inline-block" }} />Forms OK
    </span>
  );
  if (status === "ERROR") return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#dc2626", display: "inline-block" }} />ERROR
    </span>
  );
  if (accessible) return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#15803d", display: "inline-block" }} />{status}
    </span>
  );
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600, background: "#fffbeb", color: "#b45309", border: "1px solid #fde68a" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#b45309", display: "inline-block" }} />{status}
    </span>
  );
}

export function MetaCampaignsTab() {
  const [activeView, setActiveView] = useState("campaigns");
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");
  const [campaignTypeFilter, setCampaignTypeFilter] = useState("");
  const [campaignActiveFilter, setCampaignActiveFilter] = useState("");
  const [campaignFormsFilter, setCampaignFormsFilter] = useState("");
  const [campaignDateFrom, setCampaignDateFrom] = useState("");
  const [campaignDateTo, setCampaignDateTo] = useState("");
  const [campaignSort, setCampaignSort] = useState("newest");
  const [campaignPage, setCampaignPage] = useState(1);
  const [adsetSearch, setAdsetSearch] = useState("");
  const [adsetTypeFilter, setAdsetTypeFilter] = useState("");
  const [adsetDateFrom, setAdsetDateFrom] = useState("");
  const [adsetDateTo, setAdsetDateTo] = useState("");
  const [adsetSort, setAdsetSort] = useState("newest");
  const [adsetPage, setAdsetPage] = useState(1);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adSearch, setAdSearch] = useState("");
  const [adStatusFilter, setAdStatusFilter] = useState("");
  const [adCampaignFilter, setAdCampaignFilter] = useState("");
  const [adDateFrom, setAdDateFrom] = useState("");
  const [adDateTo, setAdDateTo] = useState("");
  const [adSort, setAdSort] = useState("newest");
  const [adPage, setAdPage] = useState(1);

  // Health check state
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthError, setHealthError] = useState("");

  // Add campaign modal
  const [addCampaignOpen, setAddCampaignOpen] = useState(false);
  const [campaignForm, setCampaignForm] = useState(EMPTY_CAMPAIGN_FORM);
  const [campaignSaving, setCampaignSaving] = useState(false);

  // Edit campaign modal
  const [editCampaign, setEditCampaign] = useState(null);
  const [editCampaignForm, setEditCampaignForm] = useState(EMPTY_CAMPAIGN_FORM);
  const [editCampaignSaving, setEditCampaignSaving] = useState(false);

  // Delete campaign
  const [deletingCampaignId, setDeletingCampaignId] = useState(null);
  const [syncingFormsId, setSyncingFormsId] = useState(null);
  const [syncingAllCampaigns, setSyncingAllCampaigns] = useState(false);

  // Expanded campaign (to see forms)
  const [expandedCampaignId, setExpandedCampaignId] = useState(null);

  // Add form modal (per campaign)
  const [addFormCampaignId, setAddFormCampaignId] = useState(null);
  const [formForm, setFormForm] = useState(EMPTY_FORM_FORM);
  const [formSaving, setFormSaving] = useState(false);

  // Delete form
  const [deletingFormId, setDeletingFormId] = useState(null);

  const adsets = useMemo(
    () => campaigns.flatMap((campaign) => (campaign.adsets || []).map((adset) => ({ ...adset, campaign }))),
    [campaigns],
  );

  const filteredAdsets = useMemo(() => {
    const query = adsetSearch.trim().toLowerCase();
    const matchingAdsets = adsets.filter((adset) => {
      const matchesSearch = !query || [
        adset.adset_id,
        adset.name,
        adset.campaign.name,
        adset.campaign.campaign_id,
      ].some((value) => String(value || "").toLowerCase().includes(query));
      const matchesType = !adsetTypeFilter
        || adset.campaign.campaign_type === adsetTypeFilter;
      return matchesSearch && matchesType && isWithinDateRange(adset.updated_at || adset.created_at, adsetDateFrom, adsetDateTo);
    });
    return sortRecords(matchingAdsets, (adset) => adset.updated_at || adset.created_at, adsetSort);
  }, [adsets, adsetSearch, adsetTypeFilter, adsetDateFrom, adsetDateTo, adsetSort]);

  const adsetFiltersActive = Boolean(adsetSearch || adsetTypeFilter || adsetDateFrom || adsetDateTo || adsetSort !== "newest");

  const clearAdsetFilters = () => {
    setAdsetSearch("");
    setAdsetTypeFilter("");
    setAdsetDateFrom("");
    setAdsetDateTo("");
    setAdsetSort("newest");
  };

  const filteredAds = useMemo(() => {
    const query = adSearch.trim().toLowerCase();
    const matchingAds = ads.filter((ad) => {
      const matchesSearch = !query || [
        ad.ad_id,
        ad.name,
        ad.creative_id,
        ad.creative_name,
        ad.creative_title,
        ad.campaign_id,
        ad.campaign_name,
        ad.adset_id,
        ad.adset_name,
      ].some((value) => String(value || "").toLowerCase().includes(query));
      const matchesStatus = !adStatusFilter || ad.effective_status === adStatusFilter;
      const matchesCampaign = !adCampaignFilter || ad.campaign_id === adCampaignFilter;
      return matchesSearch && matchesStatus && matchesCampaign && isWithinDateRange(ad.meta_created_at, adDateFrom, adDateTo);
    });
    return sortRecords(matchingAds, (ad) => ad.meta_created_at, adSort);
  }, [ads, adSearch, adStatusFilter, adCampaignFilter, adDateFrom, adDateTo, adSort]);

  const adFiltersActive = Boolean(adSearch || adStatusFilter || adCampaignFilter || adDateFrom || adDateTo || adSort !== "newest");
  const adStatuses = useMemo(
    () => [...new Set(ads.map((ad) => ad.effective_status).filter(Boolean))].sort(),
    [ads],
  );

  const clearAdFilters = () => {
    setAdSearch("");
    setAdStatusFilter("");
    setAdCampaignFilter("");
    setAdDateFrom("");
    setAdDateTo("");
    setAdSort("newest");
  };

  const filteredCampaigns = useMemo(() => {
    const query = campaignSearch.trim().toLowerCase();
    const matchingCampaigns = campaigns.filter((campaign) => {
      const matchesSearch = !query || [campaign.campaign_id, campaign.name]
        .some((value) => String(value || "").toLowerCase().includes(query));
      const matchesType = !campaignTypeFilter || campaign.campaign_type === campaignTypeFilter;
      const matchesActive = !campaignActiveFilter
        || String(Boolean(campaign.is_active)) === campaignActiveFilter;
      const hasForms = Boolean(campaign.forms?.length);
      const matchesForms = !campaignFormsFilter
        || (campaignFormsFilter === "with" ? hasForms : !hasForms);
      return matchesSearch && matchesType && matchesActive && matchesForms
        && isWithinDateRange(campaign.updated_at || campaign.created_at, campaignDateFrom, campaignDateTo);
    });
    return sortRecords(matchingCampaigns, (campaign) => campaign.updated_at || campaign.created_at, campaignSort);
  }, [
    campaigns,
    campaignSearch,
    campaignTypeFilter,
    campaignActiveFilter,
    campaignFormsFilter,
    campaignDateFrom,
    campaignDateTo,
    campaignSort,
  ]);

  const campaignFiltersActive = Boolean(
    campaignSearch || campaignTypeFilter || campaignActiveFilter || campaignFormsFilter || campaignDateFrom || campaignDateTo || campaignSort !== "newest",
  );

  const clearCampaignFilters = () => {
    setCampaignSearch("");
    setCampaignTypeFilter("");
    setCampaignActiveFilter("");
    setCampaignFormsFilter("");
    setCampaignDateFrom("");
    setCampaignDateTo("");
    setCampaignSort("newest");
  };

  useEffect(() => setCampaignPage(1), [campaignSearch, campaignTypeFilter, campaignActiveFilter, campaignFormsFilter, campaignDateFrom, campaignDateTo, campaignSort]);
  useEffect(() => setAdsetPage(1), [adsetSearch, adsetTypeFilter, adsetDateFrom, adsetDateTo, adsetSort]);
  useEffect(() => setAdPage(1), [adSearch, adStatusFilter, adCampaignFilter, adDateFrom, adDateTo, adSort]);
  useEffect(() => setCampaignPage((page) => Math.min(page, Math.max(1, Math.ceil(filteredCampaigns.length / PAGE_SIZE)))), [filteredCampaigns.length]);
  useEffect(() => setAdsetPage((page) => Math.min(page, Math.max(1, Math.ceil(filteredAdsets.length / PAGE_SIZE)))), [filteredAdsets.length]);
  useEffect(() => setAdPage((page) => Math.min(page, Math.max(1, Math.ceil(filteredAds.length / PAGE_SIZE)))), [filteredAds.length]);

  const pagedCampaigns = filteredCampaigns.slice((campaignPage - 1) * PAGE_SIZE, campaignPage * PAGE_SIZE);
  const pagedAdsets = filteredAdsets.slice((adsetPage - 1) * PAGE_SIZE, adsetPage * PAGE_SIZE);
  const pagedAds = filteredAds.slice((adPage - 1) * PAGE_SIZE, adPage * PAGE_SIZE);

  // Build health lookup: campaign_id → health data
  const healthByCampaignId = useMemo(() => {
    if (!health) return {};
    const map = {};
    for (const c of health.campaigns) map[c.campaign_id] = c;
    return map;
  }, [health]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminJson("/api/crm/meta-campaigns/");
      setCampaigns(data.results || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAds = async () => {
    setAdsLoading(true);
    try {
      const data = await fetchAdminJson("/api/crm/meta-campaigns/ads/");
      setAds(data.results || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setAdsLoading(false);
    }
  };

  useEffect(() => { loadCampaigns(); }, []);
  useEffect(() => {
    if (activeView === "ads") loadAds();
  }, [activeView]);

  const checkHealth = async () => {
    setHealthLoading(true);
    setHealthError("");
    try {
      const data = await fetchAdminJson("/api/crm/meta-campaigns/health/");
      setHealth(data);
    } catch (err) {
      setHealthError(err.message);
    } finally {
      setHealthLoading(false);
    }
  };

  const showNotice = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 3000);
  };

  // --- Campaign CRUD ---
  const handleAddCampaign = async (e) => {
    e.preventDefault();
    setCampaignSaving(true);
    setError("");
    try {
      const data = await fetchAdminJson("/api/crm/meta-campaigns/", {
        method: "POST",
        body: JSON.stringify(campaignForm),
      });
      setCampaignForm(EMPTY_CAMPAIGN_FORM);
      setAddCampaignOpen(false);
      await loadCampaigns();
      const synced = data.forms_synced ?? 0;
      const syncErr = data.forms_sync_error;
      if (syncErr) {
        showNotice(`Campaign created. Form sync failed: ${syncErr}`);
      } else {
        showNotice(`Campaign created. ${synced} form${synced !== 1 ? "s" : ""} auto-fetched from Facebook.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setCampaignSaving(false);
    }
  };

  const handleSyncForms = async (campaign) => {
    setSyncingFormsId(campaign.id);
    setError("");
    try {
      const data = await fetchAdminJson(`/api/crm/meta-campaigns/${campaign.id}/sync-forms/`, {
        method: "POST",
      });
      await loadCampaigns();
      if (activeView === "ads") await loadAds();
      const synced = data.forms_synced ?? 0;
      const adsSynced = data.ads_synced ?? 0;
      const adsUpdated = data.ads_updated ?? 0;
      const already = data.forms_already_registered ?? 0;
      const syncErr = data.forms_sync_error;
      if (syncErr) {
        showNotice(`Sync failed: ${syncErr}`);
      } else {
        showNotice(`Synced ${adsSynced} new and ${adsUpdated} existing ads; ${synced} new forms, ${already} already registered.`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncingFormsId(null);
    }
  };

  const handleSyncAllCampaigns = async () => {
    if (!campaigns.length) return;
    setSyncingAllCampaigns(true);
    setError("");
    let synced = 0;
    let adsSynced = 0;
    let adsUpdated = 0;
    const failures = [];
    try {
      for (const campaign of campaigns) {
        const data = await fetchAdminJson(`/api/crm/meta-campaigns/${campaign.id}/sync-forms/`, {
          method: "POST",
        });
        synced += data.forms_synced ?? 0;
        adsSynced += data.ads_synced ?? 0;
        adsUpdated += data.ads_updated ?? 0;
        if (data.forms_sync_error) failures.push(campaign.name);
      }
      await loadCampaigns();
      if (activeView === "ads") await loadAds();
      showNotice(
        failures.length
          ? `Sync completed with errors for: ${failures.join(", ")}.`
          : `Meta sync complete. ${adsSynced} new and ${adsUpdated} existing ads; ${synced} new forms.`,
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncingAllCampaigns(false);
    }
  };

  const openEditCampaign = (campaign) => {
    setEditCampaign(campaign);
    setEditCampaignForm({
      campaign_id: campaign.campaign_id,
      name: campaign.name,
      campaign_type: campaign.campaign_type,
      is_active: campaign.is_active,
    });
  };

  const handleEditCampaign = async (e) => {
    e.preventDefault();
    setEditCampaignSaving(true);
    setError("");
    try {
      await fetchAdminJson(`/api/crm/meta-campaigns/${editCampaign.id}/`, {
        method: "PATCH",
        body: JSON.stringify(editCampaignForm),
      });
      setEditCampaign(null);
      await loadCampaigns();
      showNotice("Campaign updated.");
    } catch (err) {
      setError(err.message);
    } finally {
      setEditCampaignSaving(false);
    }
  };

  const handleDeleteCampaign = async (campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"? This cannot be undone.`)) return;
    setDeletingCampaignId(campaign.id);
    setError("");
    try {
      await fetchAdminJson(`/api/crm/meta-campaigns/${campaign.id}/`, { method: "DELETE" });
      await loadCampaigns();
      showNotice("Campaign deleted.");
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingCampaignId(null);
    }
  };

  // --- Form CRUD ---
  const handleAddForm = async (e) => {
    e.preventDefault();
    setFormSaving(true);
    setError("");
    try {
      await fetchAdminJson(`/api/crm/meta-campaigns/${addFormCampaignId}/forms/`, {
        method: "POST",
        body: JSON.stringify(formForm),
      });
      setFormForm(EMPTY_FORM_FORM);
      setAddFormCampaignId(null);
      await loadCampaigns();
      showNotice("Form added.");
    } catch (err) {
      setError(err.message);
    } finally {
      setFormSaving(false);
    }
  };

  const handleDeleteForm = async (campaignId, formId) => {
    if (!window.confirm("Remove this form from the campaign?")) return;
    setDeletingFormId(formId);
    setError("");
    try {
      await fetchAdminJson(`/api/crm/meta-campaigns/${campaignId}/forms/${formId}/`, {
        method: "DELETE",
      });
      await loadCampaigns();
      showNotice("Form removed.");
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingFormId(null);
    }
  };

  return (
    <div className="settings-stack">
      {error && <div className="message-banner error">{error}</div>}
      {notice && <div className="message-banner success">{notice}</div>}

      {/* Add Campaign disclosure */}
      <AdminDisclosureCard
        title="Add Meta Campaign"
        description="Register a new Meta (Facebook) campaign and classify it as B2C or Franchise."
        actionLabel="Open Form"
        open={addCampaignOpen}
        onToggle={() => setAddCampaignOpen((v) => !v)}
      >
        <form className="form-grid settings-form-card-inner" onSubmit={handleAddCampaign}>
          <label className="field-stack">
            <span>Campaign ID</span>
            <input
              required
              placeholder="e.g. 120251552495470456"
              value={campaignForm.campaign_id}
              onChange={(e) => setCampaignForm({ ...campaignForm, campaign_id: e.target.value.trim() })}
            />
          </label>
          <label className="field-stack">
            <span>Campaign Name</span>
            <input
              required
              placeholder="e.g. Summer B2C 2025"
              value={campaignForm.name}
              onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
            />
          </label>
          <label className="field-stack">
            <span>Campaign Type</span>
            <select
              value={campaignForm.campaign_type}
              onChange={(e) => setCampaignForm({ ...campaignForm, campaign_type: e.target.value })}
            >
              {CAMPAIGN_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </label>
          <label className="field-stack" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={campaignForm.is_active}
              onChange={(e) => setCampaignForm({ ...campaignForm, is_active: e.target.checked })}
            />
            <span>Active</span>
          </label>
          <div className="settings-form-card-actions full-span">
            <button className="primary-button" type="submit" disabled={campaignSaving}>
              {campaignSaving ? "Creating..." : "Create Campaign"}
            </button>
          </div>
        </form>
      </AdminDisclosureCard>

      <div className="report-tab-strip settings-tab-strip" style={{ marginBottom: 0 }}>
        <button type="button" className={`report-tab-button ${activeView === "campaigns" ? "active" : ""}`} onClick={() => setActiveView("campaigns")}>Campaigns</button>
        <button type="button" className={`report-tab-button ${activeView === "adsets" ? "active" : ""}`} onClick={() => setActiveView("adsets")}>Ad Sets <span>{adsets.length}</span></button>
        <button type="button" className={`report-tab-button ${activeView === "ads" ? "active" : ""}`} onClick={() => setActiveView("ads")}>Ads &amp; Creatives <span>{ads.length}</span></button>
      </div>

      {/* Campaigns table */}
      {activeView === "campaigns" && <div className="settings-card settings-table-card">
        <div className="settings-card-header">
          <div>
            <p className="login-kicker">Meta Campaigns</p>
            <h3>All Campaigns</h3>
            {health && (
              <p style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>
                Last checked: {new Date(health.checked_at).toLocaleString()}
              </p>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="agent-badge mint">
              {campaignFiltersActive ? `${filteredCampaigns.length} of ${campaigns.length}` : `${campaigns.length} total`}
            </span>
            <button
              type="button"
              className="secondary-ghost compact-button"
              style={{ fontSize: 12 }}
              disabled={healthLoading}
              onClick={checkHealth}
            >
              {healthLoading ? "Checking…" : "Check Health"}
            </button>
          </div>
        </div>
        {healthError && <div className="message-banner error" style={{ margin: "0 0 12px" }}>{healthError}</div>}

        <div className="meta-campaign-filter-grid">
          <div className="search-box" style={{ minHeight: 42 }}>
            <FiSearch aria-hidden="true" />
            <input
              value={campaignSearch}
              onChange={(event) => setCampaignSearch(event.target.value)}
              placeholder="Search campaign ID or name"
              aria-label="Search campaigns by ID or name"
            />
          </div>
          <select
            value={campaignTypeFilter}
            onChange={(event) => setCampaignTypeFilter(event.target.value)}
            aria-label="Filter campaigns by type"
          >
            <option value="">All types</option>
            {CAMPAIGN_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            value={campaignActiveFilter}
            onChange={(event) => setCampaignActiveFilter(event.target.value)}
            aria-label="Filter campaigns by active status"
          >
            <option value="">All statuses</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select
            value={campaignFormsFilter}
            onChange={(event) => setCampaignFormsFilter(event.target.value)}
            aria-label="Filter campaigns by form registration"
          >
            <option value="">All form states</option>
            <option value="with">With forms</option>
            <option value="without">Without forms</option>
          </select>
          <label className="meta-date-filter"><span>From</span><input type="date" value={campaignDateFrom} onChange={(event) => setCampaignDateFrom(event.target.value)} aria-label="Campaigns from date" /></label>
          <label className="meta-date-filter"><span>To</span><input type="date" value={campaignDateTo} onChange={(event) => setCampaignDateTo(event.target.value)} aria-label="Campaigns to date" /></label>
          <select value={campaignSort} onChange={(event) => setCampaignSort(event.target.value)} aria-label="Sort campaigns by date">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <button
            type="button"
            className="secondary-ghost compact-button"
            onClick={clearCampaignFilters}
            disabled={!campaignFiltersActive}
            title="Clear campaign filters"
            aria-label="Clear campaign filters"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>

        {loading ? (
          <p style={{ padding: "16px", color: "var(--text-secondary)", fontSize: 13 }}>Loading…</p>
        ) : campaigns.length === 0 ? (
          <p style={{ padding: "16px", color: "var(--text-secondary)", fontSize: 13 }}>No campaigns yet. Add one above.</p>
        ) : filteredCampaigns.length === 0 ? (
          <p style={{ padding: "24px 16px", color: "var(--text-secondary)", fontSize: 13, textAlign: "center" }}>
            No campaigns match the selected filters.
          </p>
        ) : (
          <>
          <div className="table-scroll report-table-scroll">
            <table className="report-table">
              <thead>
                <tr>
                  <th>Campaign ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Active</th>
                  <th>Meta Status</th>
                  <th>Forms</th>
                  <th>Leads</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedCampaigns.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    <tr>
                      <td>
                        <code style={{ fontSize: 12 }}>{campaign.campaign_id}</code>
                      </td>
                      <td><strong>{campaign.name}</strong></td>
                      <td>{campaignTypeBadge(campaign.campaign_type)}</td>
                      <td>
                        {campaign.is_active
                          ? <span className="agent-badge mint">Active</span>
                          : <span className="agent-badge" style={{ background: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" }}>Inactive</span>}
                      </td>
                      <td>
                        {healthByCampaignId[campaign.campaign_id]
                          ? campaignHealthBadge(
                              healthByCampaignId[campaign.campaign_id].status,
                              healthByCampaignId[campaign.campaign_id].accessible,
                              healthByCampaignId[campaign.campaign_id].forms?.length > 0 &&
                                healthByCampaignId[campaign.campaign_id].forms.every(f => f.accessible),
                            )
                          : <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>—</span>}
                        {healthByCampaignId[campaign.campaign_id]?.error && (
                          <span title={healthByCampaignId[campaign.campaign_id].error} style={{ fontSize: 10, color: "#dc2626", marginLeft: 4, cursor: "help" }}>ⓘ</span>
                        )}
                      </td>
                      <td>
                        <button
                          type="button"
                          className="secondary-ghost compact-button"
                          style={{ fontSize: 12 }}
                          onClick={() => setExpandedCampaignId(expandedCampaignId === campaign.id ? null : campaign.id)}
                        >
                          {campaign.forms?.length || 0} form{campaign.forms?.length !== 1 ? "s" : ""}{" "}
                          {expandedCampaignId === campaign.id ? "▲" : "▼"}
                        </button>
                      </td>
                      <td>{campaign.lead_count ?? "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="secondary-ghost compact-button"
                            style={{ fontSize: 12 }}
                            onClick={() => openEditCampaign(campaign)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="secondary-ghost compact-button"
                            style={{ fontSize: 12, color: "var(--primary, #2563eb)" }}
                            disabled={syncingFormsId === campaign.id}
                            onClick={() => handleSyncForms(campaign)}
                            title="Re-fetch ads, creatives, ad sets, and forms from Meta"
                          >
                            {syncingFormsId === campaign.id ? "Syncing…" : "Sync Meta"}
                          </button>
                          <button
                            type="button"
                            className="secondary-ghost compact-button"
                            style={{ fontSize: 12, color: "var(--danger, #dc2626)" }}
                            disabled={deletingCampaignId === campaign.id}
                            onClick={() => handleDeleteCampaign(campaign)}
                          >
                            {deletingCampaignId === campaign.id ? "…" : "Delete"}
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded forms sub-row */}
                    {expandedCampaignId === campaign.id && (
                      <tr>
                        <td colSpan={8} style={{ background: "var(--surface-2, #f8fafc)", padding: "12px 20px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                            <strong style={{ fontSize: 13 }}>Forms under this campaign</strong>
                            <button
                              type="button"
                              className="secondary-ghost compact-button"
                              style={{ fontSize: 12 }}
                              onClick={() => { setAddFormCampaignId(campaign.id); setFormForm(EMPTY_FORM_FORM); }}
                            >
                              + Add Form
                            </button>
                          </div>
                          {!campaign.forms || campaign.forms.length === 0 ? (
                            <p style={{ fontSize: 12, color: "var(--text-secondary)" }}>No forms linked yet.</p>
                          ) : (
                            <table className="report-table" style={{ fontSize: 12 }}>
                              <thead>
                                <tr>
                                  <th>Form ID</th>
                                  <th>Form Name</th>
                                  <th>Meta Status</th>
                                  <th style={{ textAlign: "right" }}>Remove</th>
                                </tr>
                              </thead>
                              <tbody>
                                {campaign.forms.map((f) => {
                                  const chHealth = healthByCampaignId[campaign.campaign_id];
                                  const fHealth = chHealth?.forms?.find((fh) => fh.form_id === f.form_id);
                                  return (
                                    <tr key={f.id}>
                                      <td><code>{f.form_id}</code></td>
                                      <td>{f.name}</td>
                                      <td>
                                        {fHealth
                                          ? campaignHealthBadge(fHealth.status, fHealth.accessible)
                                          : <span style={{ color: "var(--text-secondary)" }}>—</span>}
                                        {fHealth?.error && (
                                          <span title={fHealth.error} style={{ fontSize: 10, color: "#dc2626", marginLeft: 4, cursor: "help" }}>ⓘ</span>
                                        )}
                                      </td>
                                      <td style={{ textAlign: "right" }}>
                                        <button
                                          type="button"
                                          className="secondary-ghost compact-button"
                                          style={{ fontSize: 11, color: "var(--danger, #dc2626)" }}
                                          disabled={deletingFormId === f.id}
                                          onClick={() => handleDeleteForm(campaign.id, f.id)}
                                        >
                                          {deletingFormId === f.id ? "…" : "Remove"}
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <MetaPagination total={filteredCampaigns.length} page={campaignPage} onPageChange={setCampaignPage} />
          </>
        )}
      </div>}

      {activeView === "adsets" && <div className="settings-card settings-table-card">
        <div className="settings-card-header">
          <div><p className="login-kicker">Meta Campaigns</p><h3>Ad Sets</h3></div>
          <div style={{ display: "flex", gap: 8 }}>
            <span className="agent-badge mint">
              {adsetFiltersActive ? `${filteredAdsets.length} of ${adsets.length}` : `${adsets.length} total`}
            </span>
            <button type="button" className="secondary-ghost compact-button" onClick={loadCampaigns} disabled={loading || syncingAllCampaigns}>{loading ? "Refreshing..." : "Refresh"}</button>
            <button type="button" className="primary-button compact-button" onClick={handleSyncAllCampaigns} disabled={loading || syncingAllCampaigns || !campaigns.length}>
              {syncingAllCampaigns ? "Syncing..." : "Sync All Campaigns"}
            </button>
          </div>
        </div>
        <div className="meta-adset-filter-grid">
          <div className="search-box" style={{ minHeight: 42 }}>
            <FiSearch aria-hidden="true" />
            <input
              value={adsetSearch}
              onChange={(event) => setAdsetSearch(event.target.value)}
              placeholder="Search ad set or campaign ID/name"
              aria-label="Search ad sets by ID, name, or campaign"
            />
          </div>
          <select
            value={adsetTypeFilter}
            onChange={(event) => setAdsetTypeFilter(event.target.value)}
            aria-label="Filter ad sets by campaign type"
          >
            <option value="">All types</option>
            {CAMPAIGN_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <label className="meta-date-filter"><span>From</span><input type="date" value={adsetDateFrom} onChange={(event) => setAdsetDateFrom(event.target.value)} aria-label="Ad sets from date" /></label>
          <label className="meta-date-filter"><span>To</span><input type="date" value={adsetDateTo} onChange={(event) => setAdsetDateTo(event.target.value)} aria-label="Ad sets to date" /></label>
          <select value={adsetSort} onChange={(event) => setAdsetSort(event.target.value)} aria-label="Sort ad sets by date">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <button
            type="button"
            className="secondary-ghost compact-button"
            onClick={clearAdsetFilters}
            disabled={!adsetFiltersActive}
            title="Clear ad set filters"
            aria-label="Clear ad set filters"
          >
            <FiX aria-hidden="true" />
          </button>
        </div>
        {loading ? <p style={{ padding: 16, color: "var(--text-secondary)" }}>Loading...</p> : !adsets.length ? <p style={{ padding: 16, color: "var(--text-secondary)" }}>No Ad Sets discovered yet. Select a campaign and use Sync Forms to retrieve its current Meta metadata.</p> : !filteredAdsets.length ? <p style={{ padding: "24px 16px", color: "var(--text-secondary)", textAlign: "center" }}>No ad sets match the selected filters.</p> : (
          <><div className="table-scroll report-table-scroll"><table className="report-table"><thead><tr><th>Ad Set ID</th><th>Ad Set Name</th><th>Campaign</th><th>Campaign ID</th><th>Type</th><th>Updated</th></tr></thead><tbody>
            {pagedAdsets.map((adset) => <tr key={adset.id}><td><code>{adset.adset_id}</code></td><td><strong>{adset.name || "Unnamed ad set"}</strong></td><td>{adset.campaign.name}</td><td><code>{adset.campaign.campaign_id}</code></td><td>{campaignTypeBadge(adset.campaign.campaign_type)}</td><td>{adset.updated_at ? new Date(adset.updated_at).toLocaleString() : "-"}</td></tr>)}
          </tbody></table></div>
          <MetaPagination total={filteredAdsets.length} page={adsetPage} onPageChange={setAdsetPage} />
          </>
        )}
      </div>}

      {activeView === "ads" && <div className="settings-card settings-table-card meta-ads-card">
        <div className="settings-card-header">
          <div><p className="login-kicker">Meta Campaigns</p><h3>Ads &amp; Creatives</h3></div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="agent-badge mint">
              {adFiltersActive ? `${filteredAds.length} of ${ads.length}` : `${ads.length} total`}
            </span>
            <button type="button" className="secondary-ghost compact-button" onClick={loadAds} disabled={adsLoading || syncingAllCampaigns}>{adsLoading ? "Refreshing..." : "Refresh"}</button>
            <button type="button" className="primary-button compact-button" onClick={handleSyncAllCampaigns} disabled={adsLoading || syncingAllCampaigns || !campaigns.length}>
              {syncingAllCampaigns ? "Syncing..." : "Sync All Campaigns"}
            </button>
          </div>
        </div>
        <div className="meta-ad-filter-grid">
          <div className="search-box" style={{ minHeight: 42 }}>
            <FiSearch aria-hidden="true" />
            <input value={adSearch} onChange={(event) => setAdSearch(event.target.value)} placeholder="Search ad, creative, campaign, or ad set" aria-label="Search ads and creatives" />
          </div>
          <select value={adCampaignFilter} onChange={(event) => setAdCampaignFilter(event.target.value)} aria-label="Filter ads by campaign">
            <option value="">All campaigns</option>
            {campaigns.map((campaign) => <option key={campaign.id} value={campaign.campaign_id}>{campaign.name} ({campaign.campaign_id})</option>)}
          </select>
          <select value={adStatusFilter} onChange={(event) => setAdStatusFilter(event.target.value)} aria-label="Filter ads by Meta status">
            <option value="">All Meta statuses</option>
            {adStatuses.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
          <label className="meta-date-filter"><span>From</span><input type="date" value={adDateFrom} onChange={(event) => setAdDateFrom(event.target.value)} aria-label="Ads from date" /></label>
          <label className="meta-date-filter"><span>To</span><input type="date" value={adDateTo} onChange={(event) => setAdDateTo(event.target.value)} aria-label="Ads to date" /></label>
          <select value={adSort} onChange={(event) => setAdSort(event.target.value)} aria-label="Sort ads by Meta creation date">
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
          <button type="button" className="secondary-ghost compact-button" onClick={clearAdFilters} disabled={!adFiltersActive} title="Clear ad filters" aria-label="Clear ad filters"><FiX aria-hidden="true" /></button>
        </div>
        {adsLoading ? <p style={{ padding: 16, color: "var(--text-secondary)" }}>Loading ads and creatives...</p> : !ads.length ? <p style={{ padding: 16, color: "var(--text-secondary)" }}>No ads have been synced yet. Use Sync All Campaigns to retrieve ads and creatives from Meta.</p> : !filteredAds.length ? <p style={{ padding: "24px 16px", color: "var(--text-secondary)", textAlign: "center" }}>No ads match the selected filters.</p> : (
          <><div className="table-scroll report-table-scroll"><table className="report-table"><thead><tr><th>Creative</th><th>Ad</th><th>Campaign / Ad Set</th><th>Meta Status</th><th>Created on Meta</th><th>Updated on Meta</th></tr></thead><tbody>
            {pagedAds.map((ad) => {
              const preview = ad.creative_image_url || ad.creative_thumbnail_url;
              return <tr key={ad.id}>
                <td><div className="meta-creative-cell">
                  {preview ? <img src={preview} alt="" className="meta-creative-thumb" loading="lazy" /> : <div className="meta-creative-thumb empty">No preview</div>}
                  <div><strong>{ad.creative_title || ad.creative_name || "Unnamed creative"}</strong><div className="meta-record-id">Creative ID: {ad.creative_id || "-"}</div>{ad.creative_body && <div className="meta-creative-copy" title={ad.creative_body}>{ad.creative_body}</div>}</div>
                </div></td>
                <td><strong>{ad.name || "Unnamed ad"}</strong><div className="meta-record-id">{ad.ad_id}</div></td>
                <td><strong>{ad.campaign_name}</strong><div className="meta-record-id">{ad.campaign_id}</div><div className="meta-secondary-line">{ad.adset_name || "Unknown ad set"}</div><div className="meta-record-id">{ad.adset_id || "-"}</div></td>
                <td><span className={`agent-badge ${ad.effective_status === "ACTIVE" ? "mint" : ""}`}>{ad.effective_status || ad.status || "Unknown"}</span></td>
                <td style={{ whiteSpace: "nowrap" }}>{ad.meta_created_at ? new Date(ad.meta_created_at).toLocaleString() : "-"}</td>
                <td style={{ whiteSpace: "nowrap" }}>{ad.meta_updated_at ? new Date(ad.meta_updated_at).toLocaleString() : "-"}</td>
              </tr>;
            })}
          </tbody></table></div>
          <MetaPagination total={filteredAds.length} page={adPage} onPageChange={setAdPage} />
          </>
        )}
      </div>}

      {/* Edit Campaign Modal */}
      {editCampaign && (
        <div className="modal-backdrop" onClick={() => setEditCampaign(null)}>
          <div className="modal-panel" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <p className="modal-kicker">Meta Campaign</p>
                <h2>Edit Campaign</h2>
              </div>
              <button type="button" className="close-button" onClick={() => setEditCampaign(null)}>✕</button>
            </div>
            <form onSubmit={handleEditCampaign}>
              <div className="form-grid" style={{ gap: 14, padding: "4px 0 20px" }}>
                <label className="field-stack">
                  <span>Campaign ID</span>
                  <input
                    required
                    value={editCampaignForm.campaign_id}
                    onChange={(e) => setEditCampaignForm({ ...editCampaignForm, campaign_id: e.target.value.trim() })}
                  />
                </label>
                <label className="field-stack">
                  <span>Campaign Name</span>
                  <input
                    required
                    value={editCampaignForm.name}
                    onChange={(e) => setEditCampaignForm({ ...editCampaignForm, name: e.target.value })}
                  />
                </label>
                <label className="field-stack">
                  <span>Campaign Type</span>
                  <select
                    value={editCampaignForm.campaign_type}
                    onChange={(e) => setEditCampaignForm({ ...editCampaignForm, campaign_type: e.target.value })}
                  >
                    {CAMPAIGN_TYPE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </label>
                <label className="field-stack" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={editCampaignForm.is_active}
                    onChange={(e) => setEditCampaignForm({ ...editCampaignForm, is_active: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary-ghost" onClick={() => setEditCampaign(null)}>Cancel</button>
                <button type="submit" className="primary-button modal-submit" disabled={editCampaignSaving}>
                  {editCampaignSaving ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Form Modal */}
      {addFormCampaignId && (
        <div className="modal-backdrop" onClick={() => setAddFormCampaignId(null)}>
          <div className="modal-panel" style={{ maxWidth: 420 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <p className="modal-kicker">Meta Form</p>
                <h2>Add Form to Campaign</h2>
              </div>
              <button type="button" className="close-button" onClick={() => setAddFormCampaignId(null)}>✕</button>
            </div>
            <form onSubmit={handleAddForm}>
              <div className="form-grid" style={{ gap: 14, padding: "4px 0 20px" }}>
                <label className="field-stack">
                  <span>Form ID</span>
                  <input
                    required
                    placeholder="e.g. 1316930146981840"
                    value={formForm.form_id}
                    onChange={(e) => setFormForm({ ...formForm, form_id: e.target.value.trim() })}
                  />
                </label>
                <label className="field-stack">
                  <span>Form Name</span>
                  <input
                    required
                    placeholder="e.g. Franchise Inquiry Form"
                    value={formForm.name}
                    onChange={(e) => setFormForm({ ...formForm, name: e.target.value })}
                  />
                </label>
              </div>
              <div className="modal-actions">
                <button type="button" className="secondary-ghost" onClick={() => setAddFormCampaignId(null)}>Cancel</button>
                <button type="submit" className="primary-button modal-submit" disabled={formSaving}>
                  {formSaving ? "Adding…" : "Add Form"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MetaCampaignsTab;
